import React, { useState, useRef, useEffect, useMemo, memo } from 'react';
import { Box, Button, CircularProgress, IconButton, Menu, MenuItem, Slider, Stack, Tooltip } from '@mui/material';
import { ReactComponent as DownloadIcon } from '../../../../static/icons/screen-recording.svg';
import { ReactComponent as PauseIcon } from '../../../../static/icons/rec-pause.svg';
import { ReactComponent as PlayIcon } from '../../../../static/icons/rec-play.svg';
import { ReactComponent as PlayLiveIcon } from '../../../../static/icons/play.svg';
import { ReactComponent as SkipBackIcon } from '../../../../static/icons/rec-rev.svg';
import { ReactComponent as SkipForwardIcon } from '../../../../static/icons/rec-forward.svg'
import { ReactComponent as FullScreeIcon } from '../../../../static/icons/rec-fullscreen.svg'
import { ReactComponent as FullScreeLiveIcon } from '../../../../static/icons/live-fullscreen.svg'
import { ReactComponent as CloseIcon } from '../../../../static/icons/closewidget.svg'

import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { SC_RECORDING_URL } from '../../../../utils/properties';
import { getSecuredRecordingUrl } from '../../../../services/api-service';
import { widgetService } from '../../../../services/widgets-service';

interface VideoPlayerProps {
  url: string;
  agentId?: string;
  width?: number;
  height?: number;
  interval?: number;
  failCount?: number;
  isLive?: boolean;
  onlyBargeinMode?: boolean;
  agentName?: string;
}

interface VideoPlayerIconProps {
  row: {
    videoRecordingURL?: string;
    VideoRecordingURL?: string;
    liveRecordingData?: any;
  };
  icon?: React.ReactNode | undefined;
  isLive?: boolean;
  onClose?: () => void;
  defaultOpen?: boolean;
  hideIcon?: boolean;
  fullscreenContainer?: any
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    opacity: '.2 !important'
  },
  '& .MuiPaper-root': {
    borderRadius: '12px',
    width: '600px',
    maxWidth: '600px',
    maxHeight: '493px',

  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    backgroundColor: '#1D242D',
    paddingBottom: '0px'
  },
  '& .MuiDialogTitle-root': {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  }

}));

const CustomSlider = styled(Slider)(({ theme }) => ({
  paddingTop: '5px',
  paddingBottom: '5px',
  color: '#99A0A8',
  height: 4,
  '& .MuiSlider-thumb': {
    width: 10,
    height: 10,
    background: 'conic-gradient(from 180deg at 50% 50%, #D9D9D9 0deg, #737373 360deg)',
    transition: '.4s cubic-bezier(.47,1.64,.41,.8)',
    '&::before': {
      boxShadow: '0 2px 12px 0 rgba(0,0,0,0.4)',
    },

    '&.Mui-active': {
      width: 20,
      height: 20,
    },
  },
  '& .MuiSlider-rail': {
    opacity: .28,
  },

}));

// Styled components for fullscreen controls
const FullscreenControlsOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
  padding: '20px',
  transition: 'opacity 0.3s ease-in-out',
  zIndex: 1000,
}));

const FullscreenVideoContainer = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '100%',
  cursor: 'none',
  '&:hover': {
    cursor: 'default',
  }
});

let liveFeedInterval: any = null;
const DUPLICATE_THRESHOLD = 10;
let lastImageHash = '';
let duplicateImageCount = 0;

// VideoPlayer Component
const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url = '', agentId, width = 800, height = 380, interval = 1, failCount = 3, isLive = false, onlyBargeinMode = false, agentName = ''
}) => {
  const [isPlaying, setIsPlaying] = useState(isLive);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string>('');
  const [failCounter, setFailCounter] = useState<number>(0);
  const [imageSource, setImageSource] = useState<string>('');
  const [isScreenSharingRequested, setIsScreenSharingRequested] = useState<boolean>(isLive);

  // Fullscreen control states
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const iv = new Uint8Array([..."kpt9d16c511b0a3cab4f5fe2fd5d6a434fa"].map((char) => char.charCodeAt(0)));
  const algorithm = { name: "AES-GCM", length: 256 };
  const salt = new Uint8Array([...atob("98HLecrKyCHL7HAdv4Cb7qKT1py3Opd88ZqMutsspvk=")].map((char) => char.charCodeAt(0)));

  const deriveKey = async () => {
    return window.crypto.subtle.importKey("raw", salt, algorithm.name, false, ["encrypt", "decrypt"]);
  };

  const generateToken = async (message: string) => {
    const derived = await deriveKey();
    const encoded = new Uint8Array([...message].map((char) => char.charCodeAt(0)));
    const encrypted = await window.crypto.subtle.encrypt({ name: algorithm.name, iv }, derived, encoded);
    return btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  };

  const generateImageHash = async (blob: Blob): Promise<string> => {
    const arrayBuffer = await blob.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const size = uint8Array.length;
    const sample = uint8Array.slice(0, Math.min(1000, size));

    let hash = size.toString();
    for (let i = 0; i < sample.length; i += 10) {
      hash += sample[i].toString(16);
    }

    return hash;
  };

  const streamAgentFeed = async () => {
    if (!agentId) {
      handleError("Error. Invalid input passed, retry again");
      return;
    }

    // Check if we've hit the duplicate threshold
    if (onlyBargeinMode && duplicateImageCount >= DUPLICATE_THRESHOLD) {
      forceStopLiveStream()
      return;
    }

    const ts = new Date().getTime();
    try {
      const response = await fetch(`${url}/files?live=1&agentId=${agentId}&date=${ts}`, {
        method: 'GET',
        headers: {
          'x-api-token': await generateToken(`${agentId}:${ts}`)
        }
      });
      if (!response.ok) throw new Error('Failed to fetch image');
      const blob = await response.blob();
      const currentImageHash = await generateImageHash(blob);
      if (onlyBargeinMode && lastImageHash && currentImageHash === lastImageHash) {
        duplicateImageCount++;
        if (duplicateImageCount >= DUPLICATE_THRESHOLD) {
          forceStopLiveStream()
          return;
        }
      } else {
        duplicateImageCount = 0;
      }

      lastImageHash = currentImageHash;
      setImageSource(URL.createObjectURL(blob));
      setError('');
      setFailCounter(0);
    } catch (error) {
      console.error(error);
      handleError("Error. Paused due to streaming error. Click on Play again to Retry");
    }
  };

  const handleError = (message: string) => {
    setError(message);
    setIsPlaying(false);
    setFailCounter((prev) => prev + 1);
  };

  // Handle mouse movement for fullscreen controls
  const handleMouseMove = () => {
    if (isFullscreen) {
      setShowControls(true);

      // Clear existing timeout
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }

      // Set new timeout to hide controls after 3 seconds
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  // Handle mouse leave for fullscreen container
  const handleMouseLeave = () => {
    if (isFullscreen) {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
      hideControlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (onlyBargeinMode) {
      requestScreenShare(true)
    }
    return () => {
      requestScreenShare(false)
    }
  }, [onlyBargeinMode]);

  // Listen for fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);

      if (!isCurrentlyFullscreen) {
        setShowControls(true);
        if (hideControlsTimeoutRef.current) {
          clearTimeout(hideControlsTimeoutRef.current);
        }
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isLive) {
      const videoElement = videoRef.current;
      if (!videoElement) return;

      const handleTimeUpdate = () => setCurrentTime(videoElement.currentTime);
      const handleLoadedMetadata = () => setDuration(videoElement.duration);
      const handleEnded = () => setIsPlaying(false);

      videoElement.addEventListener('timeupdate', handleTimeUpdate);
      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('ended', handleEnded);

      return () => {
        videoElement.removeEventListener('timeupdate', handleTimeUpdate);
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('ended', handleEnded);
      };
    }
  }, [isLive]);

  useEffect(() => {
    if (isLive && isScreenSharingRequested) {
      if (liveFeedInterval) {
        clearInterval(liveFeedInterval);
      }
      if (isPlaying) {
        liveFeedInterval = setInterval(streamAgentFeed, interval * 1000);
      } else {
        if (liveFeedInterval) {
          clearInterval(liveFeedInterval);
        }
      }
    }
    return () => {
      if (liveFeedInterval) {
        clearInterval(liveFeedInterval);
      }
    }
  }, [isPlaying, isLive, isScreenSharingRequested]);

  useEffect(() => {
    if (error && failCounter < failCount) {
      setTimeout(() => {
        setIsPlaying(true);
      }, 1000);
    }
  }, [error, failCounter]);

  const togglePlay = () => {
    if (isLive) {
      isPlaying ? pause() : play();
    } else {
      if (!videoRef.current) return;
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  function play() {
    setFailCounter(0);
    duplicateImageCount = 0;
    lastImageHash = '';
    setIsPlaying(true);
  }

  const pause = () => {
    setIsPlaying(false);
  };

  const skip = (seconds: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  function forceStopLiveStream() {
    handleError("Agent recording has ended or did not share screen");
    setIsScreenSharingRequested(false);
    setIsPlaying(false);
    if (liveFeedInterval) {
      clearInterval(liveFeedInterval);
    }
  }

  const formatTime = (time: number): string => {
    try {
      // Input validation
      if (time == null || isNaN(time) || !isFinite(time)) {
        return '00:00:00';
      }

      // Handle negative time
      if (time < 0) {
        return '00:00:00';
      }

      const totalSeconds = Math.floor(time);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      // Validate calculated values
      if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) {
        return '00:00:00';
      }

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    } catch (error) {
      // Log error for debugging but return safe default
      console.warn('Error formatting time:', error, 'Input:', time);
      return '00:00';
    }
  };

  if (!url) {
    return (
      <Box display={'flex'} gap={1} justifyContent={'center'} p={4} >
        <Typography fontWeight={600} color={'white'}>Error:</Typography>
        <Typography fontWeight={400} color={'white'}>No video URL provided.</Typography>
      </Box>
    );
  }

  function showFullscreenDiv() {
    if (document.fullscreenElement) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
      return;
    }
    const fullscreenDiv = document.getElementById("ozScreenRecVideoPlayer");

    if (fullscreenDiv) {
      fullscreenDiv.requestFullscreen();
    } else {
      console.error("Fullscreen div not found.");
    }
  }

  async function requestScreenShare(status: boolean = true) {
    try {
      if (onlyBargeinMode) {
        const response = await widgetService({
          method: 'POST',
          responseType: 'string',
          bodyParams: {
            "action": status ? "agentBarginStart" : "agentBarginEnd",
            "apiId": 59,
            "agentId": agentName,
            "agentUniqueId": agentId,
          },
        });
        const responseString = await response?.text();
        if (responseString && responseString === "success") {
          if (status) {
            play();
          } else {
            pause();
          }
          setIsScreenSharingRequested(status);
        }
      }
    } catch (error) {
      console.error("Failed to request screen share", error);
    } finally {

    }
  }

  // Controls component that can be reused
  const VideoControls = ({ isFullscreenMode = false }: { isFullscreenMode?: boolean }) => (
    <Box sx={{
      color: isFullscreenMode ? 'white' : 'inherit',
      ...(isFullscreenMode && { px: 2, pb: 2 })
    }}>
      {!isLive && (
        <Stack sx={{ paddingLeft: '2px', paddingRight: '8px' }}>
          <CustomSlider
            aria-label="time-indicator"
            size="medium"
            value={currentTime}
            min={0}
            step={.1}
            max={duration}
            onChange={(_, value) => {
              setCurrentTime(value as number);
              if (videoRef.current) {
                videoRef.current.currentTime = parseFloat(value.toString());
              }
            }}
            sx={isFullscreenMode ? {
              '& .MuiSlider-thumb': {
                color: 'white',
              },
              '& .MuiSlider-track': {
                color: 'white',
              }
            } : {}}
          />
        </Stack>
      )}
      <Stack direction={'row'} justifyContent={'space-between'} ml={-1.5} mr={-1.5}>
        <Stack direction={'row'}>
          {!onlyBargeinMode && <IconButton onClick={togglePlay} sx={{ color: isFullscreenMode ? 'white' : 'inherit' }}>
            {isPlaying ? <PauseIcon /> : isLive ? <PlayLiveIcon /> : <PlayIcon />}
          </IconButton>}
          {!isLive && (
            <IconButton onClick={() => skip(-10)} sx={{ color: isFullscreenMode ? 'white' : 'inherit' }}>
              <SkipBackIcon />
            </IconButton>
          )}
          {!isLive && (
            <IconButton onClick={() => skip(10)} sx={{ color: isFullscreenMode ? 'white' : 'inherit' }}>
              <SkipForwardIcon />
            </IconButton>
          )}
          {isLive && onlyBargeinMode && !isScreenSharingRequested && (
            <Stack direction={"row"} alignItems={'center'} gap={.3} mb={'8px'} ml={2}>
              <Button onClick={() => requestScreenShare(!isScreenSharingRequested)} sx={{ borderRadius: '8px', height: '24px', fontSize: '12px', fontWeight: '400', }} color={isScreenSharingRequested ? 'error' : 'success'} variant={isFullscreen ? 'contained' : 'outlined'}>
                {isScreenSharingRequested ? 'Cancel Screen Share' : 'Request Screen Share'}
              </Button>
            </Stack>
          )}
          {isLive && (
            <Stack direction={"row"} alignItems={'center'} gap={.3} mb={'8px'} mx={onlyBargeinMode ? 2 : 0}>
              <Box sx={{ background: "#E65B5C", width: '5px', height: '5px', borderRadius: '50%' }} />
              <Typography color={isFullscreenMode ? 'white' : '#FFFFFF'} fontWeight={400} fontSize={'10px'}>
                {onlyBargeinMode ? isScreenSharingRequested ? 'LIVE' : 'STOPPED' : 'LIVE'}
              </Typography>
            </Stack>
          )}
        </Stack>
        {isLive && error && (
          <Typography color={'#E65B5C'} fontWeight={400} fontSize={'12px'} mt="8px">
            {error}
          </Typography>
        )}
        <Stack direction={'row'} alignItems={'center'}>
          {!isLive && (
            <Typography
              color={isFullscreenMode ? 'white' : '#B2BBC6'}
              fontWeight={400}
              fontSize={'12px'}
              mb={'8px'}
            >
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          )}
          <IconButton onClick={() => showFullscreenDiv()} sx={{ color: isFullscreenMode ? 'white' : 'inherit' }}>
            {isLive ? <FullScreeLiveIcon /> : <FullScreeIcon />}
          </IconButton>
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <FullscreenVideoContainer
      id="ozScreenRecVideoPlayer"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: isFullscreen ? 'relative' : 'static',
        cursor: isFullscreen && !showControls ? 'none' : 'default',
      }}
    >
      {isLive && imageSource ? (
        <img
          src={imageSource}
          className="ozScreenRecVideoPlayer_canvas"
          width={width}
          height={height}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: isFullscreen ? '0' : '8px',
            border: isFullscreen ? 'none' : '1px solid #D6D9DC'
          }}
          alt="Live Feed"
        />
      ) : (
        <video
          ref={videoRef}
          style={{
            width: '100%',
            height: '100%',
            borderRadius: isFullscreen ? '0' : '8px',
            border: isFullscreen ? 'none' : '1px solid #D6D9DC'
          }}
        >
          <source src={url} type="video/mp4" />
        </video>
      )}

      {/* Fullscreen controls overlay */}
      {isFullscreen && (
        <FullscreenControlsOverlay
          sx={{
            opacity: showControls ? 1 : 0,
            pointerEvents: showControls ? 'auto' : 'none',
          }}
        >
          <VideoControls isFullscreenMode={true} />
        </FullscreenControlsOverlay>
      )}

      {/* Regular controls (hidden in fullscreen) */}
      {!isFullscreen && <VideoControls />}
    </FullscreenVideoContainer>
  );
};

const VideoPlayerIcon = memo(function VideoPlayerIcon({ row, icon, isLive = false, onClose, defaultOpen = false, hideIcon = false, fullscreenContainer }: VideoPlayerIconProps) {

  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [selectedUrlIndex, setSelectedUrlIndex] = useState<number | null>(null);
  const [anchorVideo, setAnchorVideo] = useState(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [secureUrl, setSecureUrl] = useState<string>('');
  const onlyBargeinMode = useMemo(() => isLive ? (row?.liveRecordingData?.onlyBargeinMode || false) : false, [row?.liveRecordingData?.onlyBargeinMode]);

  const videoUrl: string | string[] = useMemo(() => {
    const url = row?.videoRecordingURL || row?.VideoRecordingURL || '';
    return (url.includes(',') && url.split(',').length > 1) ? url.split(',') : url;
  }, [row]);
  const isMultipleVideos = Array.isArray(videoUrl);
  let selectedVideoUrl = isLive ? SC_RECORDING_URL || "" : (isMultipleVideos) ? selectedUrlIndex !== null ? videoUrl[selectedUrlIndex] : "" : videoUrl as string;
  const agentId = row?.liveRecordingData?.AgentIncId || row?.liveRecordingData?.AgentUniqueId || '';
  const agenntName = row?.liveRecordingData?.AgentId || '';
  const open = Boolean(anchorVideo);
  useEffect(() => {
    if (defaultOpen) setIsOpen(true);
  }, [defaultOpen]);

  useEffect(() => {
    try {
      if (selectedVideoUrl && !isLive && isOpen) {
        setLoading(true);
        getSecuredRecordingUrl({ audioUrl: selectedVideoUrl.trim(), type: "video" }).then((resp) => {
          if (resp?.data) {
            setSecureUrl(resp?.data);
          }
          setLoading(false);
        }).catch((error) => {
          console.error(error);
          setLoading(false);
          setSecureUrl("");
        });
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      setSecureUrl("");
    }
  }, [selectedVideoUrl, isLive, isOpen]);

  const handleClick = (event: any) => setAnchorVideo(event.currentTarget);
  const handleCloseMenu = () => setAnchorVideo(null);

  function getFileName(url: any) {
    if (!url) return url;
    try {
      const pathname = new URL(url).pathname;
      const fileName = pathname.split('/').pop();
      const fileNameWithoutExtension = fileName ? fileName.split('.').slice(0, -1).join('.') : pathname;
      let name = fileNameWithoutExtension || fileName;
      if (name === "video" || name === "video.mp4" || !name) {
        return pathname.split('/')[1].split("_")[1] || pathname; // call id or pathname
      }
      return name;
    } catch (e) {
      console.error("Failed to get Filename", e);
      return url;
    }
  }

  const videTitleStyle = useMemo(() => ({
    fontSize: '14px',
    color: '#000000',
    fontWeight: '400',
    backgroundColor: '#E6F4FF'
  }), []);

  const handleClose = () => {
    setIsOpen(false);
    setSelectedUrlIndex(null);
    setSecureUrl('');
    if (onClose) onClose();
  }

  const VideoIconButton = ({ url, index }: { url: string, index?: number }) => {
    return (
      <Tooltip title={isLive ? "" : url ? `View recording ${getFileName(url)}` : "No recording url available"}>
        <div>
          <IconButton onClick={() => {
            setIsOpen(true);
            typeof index === 'number' && setSelectedUrlIndex(index);
          }}
            disabled={isLive ? false : !Boolean(url)}
          >
            {icon || <DownloadIcon />}
          </IconButton>
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <React.Fragment>
        {!hideIcon && isMultipleVideos ? <Stack minWidth={'fit-content'} sx={{}}>
          <Box display={'flex'}>
            <div style={{ marginRight: '12px' }}>
              <VideoIconButton url={videoUrl[0] as string} index={0} />
            </div>
            <Button size="small" variant="contained" onClick={handleClick} sx={{ minWidth: 'fit-content' }}>
              +{videoUrl.length - 1}
            </Button>
            <Menu anchorEl={anchorVideo} open={open} onClose={handleCloseMenu}>
              {videoUrl.map((url: string, index: number) => (index !== 0 && <MenuItem key={index}>
                <VideoIconButton url={url} index={index} />
              </MenuItem>
              ))}
            </Menu>
          </Box>
        </Stack>
          :
          <VideoIconButton url={selectedVideoUrl as string} />
        }
        <BootstrapDialog
          container={fullscreenContainer}
          onClose={handleClose}
          aria-labelledby="customized-dialog-title"
          open={isOpen}
        >
          <DialogTitle sx={{ ...videTitleStyle, m: 0, p: 1, }} id="customized-dialog-title">
            {(selectedVideoUrl && !isLive) ? <Box display={'flex'} gap={.8} alignItems={"center"}>
              Viewing
              {<Typography sx={{ ...videTitleStyle, fontSize: '12px', fontWeight: '500', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getFileName(selectedVideoUrl)}</Typography>}
              screen
            </Box> : <Box display={'flex'} gap={.8}>
              {isLive ? (
                <>
                  Viewing{' '}
                  <span style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block' }}>
                    {row?.liveRecordingData?.AgentId}
                  </span>
                  {' '}Screen
                </>
              ) : "Video Player"}
            </Box>}
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size='small'
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 5,
            })}
          >
            <CloseIcon width={18} height={18} />
          </IconButton>
          <DialogContent dividers className='ozScreenRecVideoPlayerDialogContent'>
            {loading ? <Box display={'flex'} justifyContent={'center'} alignItems={'center'} height={'100%'} p={3}>
              <CircularProgress />
            </Box> : <VideoPlayer url={isLive ? selectedVideoUrl : secureUrl} agentId={agentId || ""} isLive={isLive} onlyBargeinMode={onlyBargeinMode} agentName={agenntName || ""} />}
          </DialogContent>
        </BootstrapDialog>
      </React.Fragment>
    </>
  );
});

export default VideoPlayerIcon;
